import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import useIsomorphicLayoutEffect from "@/hooks/global/useIsomorphicLayoutEffect";
import { getCountdownBanner } from "@/services/cms";
import { toSiteHref } from "@/utils/toSiteHref";
import {
  HEADER_BANNER_DEFAULT_COLORS,
  HEADER_BANNER_MARQUEE_SPEED_PX_PER_S,
  HEADER_BANNER_REFRESH_MS,
  HEADER_BANNER_STYLES,
  MAX_TIMEOUT_MS,
} from "@/constants/HeaderBanner";

// Matches the MW cache TTL: asking again sooner returns the same answer.
const DEDUPE_MS = 60 * 1000;

const textOf = (value) => (typeof value === "string" ? value.trim() : "");

// Magento validates colours on save; anything else falls back to the design value instead of
// reaching an inline style.
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const colorOr = (value, fallback) => (typeof value === "string" && HEX_COLOR.test(value.trim()) ? value.trim() : fallback);

const parseTime = (value) => {
  const ms = typeof value === "string" ? Date.parse(value) : NaN;
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Magento's banner (GET /cms/countdown-banner → data) to what the strip renders, or null.
 * Magento omits unset fields and empty groups, so everything but is_active is optional.
 * A config that cannot render (unknown style, counter without end, marquee without text)
 * hides the strip instead of half-showing it.
 */
function toBanner(raw) {
  if (raw?.is_active !== true) return null;
  const general = raw.general || {};
  const theme = raw.theme || {};
  const counter = raw.counter || {};
  const style = general.display_style;
  const endsAt = parseTime(general.end_at);
  const items = Array.isArray(raw.marquee?.items) ? raw.marquee.items.map(textOf).filter(Boolean) : [];

  if (style === HEADER_BANNER_STYLES.counter && endsAt === null) return null;
  if (style === HEADER_BANNER_STYLES.marquee && items.length === 0) return null;
  if (!Object.values(HEADER_BANNER_STYLES).includes(style)) return null;

  const speed = Number(raw.marquee?.speed);
  return {
    style,
    endsAt,
    text: textOf(counter.message),
    items,
    speed: speed > 0 ? speed : HEADER_BANNER_MARQUEE_SPEED_PX_PER_S,
    pauseOnHover: raw.marquee?.pause_on_hover !== false,
    href: toSiteHref(general.link_url),
    buttonLabel: textOf(counter.button_title),
    colors: {
      background: colorOr(theme.background_color, HEADER_BANNER_DEFAULT_COLORS.background),
      text: colorOr(theme.text_color, HEADER_BANNER_DEFAULT_COLORS.text),
      digitBox: colorOr(counter.box_color, HEADER_BANNER_DEFAULT_COLORS.digitBox),
      button: colorOr(counter.button_color, HEADER_BANNER_DEFAULT_COLORS.button),
      buttonText: colorOr(counter.button_text_color, HEADER_BANNER_DEFAULT_COLORS.buttonText),
    },
    images: {
      desktop: theme.desktop_background_image || "",
      mobile: theme.mobile_background_image || "",
    },
  };
}

/**
 * The campaign strip to show right now, or null. Magento decides whether a banner is live
 * when asked; this hook hides it on its own at end_at, because the answer may be up to a
 * minute old (MW cache) and an open page is not asked again at that exact moment.
 */
export default function useHeaderBanner(locale) {
  const { data } = useSWR(["countdown-banner", locale], getCountdownBanner, {
    refreshInterval: HEADER_BANNER_REFRESH_MS,
    dedupingInterval: DEDUPE_MS,
    shouldRetryOnError: false,
  });
  const banner = useMemo(() => toBanner(data?.data?.data), [data]);
  const [now, setNow] = useState(() => Date.now());

  // The answer arrives after mount: re-read the clock before paint so a banner that ended
  // in the meantime never flashes.
  useIsomorphicLayoutEffect(() => {
    setNow(Date.now());
  }, [banner]);

  useEffect(() => {
    if (!banner || banner.endsAt === null || banner.endsAt <= now) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(banner.endsAt - now, MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [banner, now]);

  useEffect(() => {
    const handleVisible = () => document.visibilityState === "visible" && setNow(Date.now());
    document.addEventListener("visibilitychange", handleVisible);
    return () => document.removeEventListener("visibilitychange", handleVisible);
  }, []);

  if (!banner || (banner.endsAt !== null && now >= banner.endsAt)) return null;
  return banner;
}
